(function () {
  const containerSelector = "[data-sticky-cta-container]";
  const visibleClass = "is-visible";
  const hiddenClass = "is-hidden";
  const shopifyCurrency = globalThis.Shopify?.currency;
  const currencyCode =
    typeof shopifyCurrency === "string"
      ? shopifyCurrency
      : shopifyCurrency?.active || "CAD";

  const formatCurrency = (valueInCents, options = {}) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currencyCode,
      ...options,
    }).format(valueInCents / 100);

  const parseMoneyToCents = (value) => {
    const parsedValue = Number.parseFloat(
      String(value || "").replace(/[^0-9.]/g, ""),
    );
    return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : 0;
  };

  const getBuyButton = () =>
    document.querySelector("[data-has-addons]") ||
    document.querySelector(".product-form__submit");

  const getBuyButtonState = (button = getBuyButton()) => {
    if (!button) {
      return null;
    }

    const label = button.querySelector("div span")?.textContent?.trim() || "";
    const disabled =
      button.disabled ||
      button.hasAttribute("disabled") ||
      button.classList.contains("disabled") ||
      button.getAttribute("aria-disabled") === "true";

    return { button, disabled, label };
  };

  const refreshAffirm = () => {
    const affirmUi = window.affirm?.ui;

    if (!affirmUi) {
      return;
    }

    if (typeof affirmUi.ready === "function") {
      affirmUi.ready(() => affirmUi.refresh?.());
      return;
    }

    affirmUi.refresh?.();
  };

  class StickyCTA {
    constructor(container) {
      this.container = container;
      this.element = container.querySelector("[data-sticky-cta]");
      this.priceElement = container.querySelector("[data-sticky-cta-price]");
      this.percentElement = container.querySelector(
        "[data-sticky-cta-percent]",
      );
      this.percentValueElement = container.querySelector(
        "[data-sticky-cta-percent-value]",
      );
      this.affirmAmountElements = container.querySelectorAll(
        ".sticky-cta__affirm-app [data-amount]",
      );
      this.submitButton = container.querySelector("[data-sticky-cta-submit]");
      this.submitButtonLabel = container.querySelector(
        "[data-sticky-cta-button-label]",
      );
      this.pricingTimer = null;
      this.ticking = false;
      this.buyButtonObserver = null;
      this.observedBuyButton = null;
      this.destroyed = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handlePriceUpdate = this.handlePriceUpdate.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    init() {
      if (
        !this.element ||
        this.container.dataset.stickyCtaInitialized === "true"
      ) {
        return;
      }

      this.container.dataset.stickyCtaInitialized = "true";
      this.container.stickyCTA = this;
      this.destroyed = false;
      this.updatePricingFromDOM();
      this.syncAvailabilityFromProductForm(
        this.container.dataset.isAvailable === "true",
      );
      this.handleScroll();

      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleViewportChange);
      document.addEventListener("variant-change", this.handleVariantChange);
      document.addEventListener(
        "buy-button-price-updated",
        this.handlePriceUpdate,
      );
      this.submitButton?.addEventListener("click", this.handleSubmit);
    }

    destroy() {
      this.destroyed = true;
      window.clearTimeout(this.pricingTimer);
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleViewportChange);
      document.removeEventListener("variant-change", this.handleVariantChange);
      document.removeEventListener(
        "buy-button-price-updated",
        this.handlePriceUpdate,
      );
      this.submitButton?.removeEventListener("click", this.handleSubmit);
      this.buyButtonObserver?.disconnect();
      this.buyButtonObserver = null;
      this.observedBuyButton = null;
      delete this.container.dataset.stickyCtaInitialized;
      delete this.container.stickyCTA;
    }

    handleSubmit() {
      const buyButton = this.observedBuyButton || getBuyButton();
      const form =
        buyButton?.form ||
        document.querySelector('.form[data-type="add-to-cart-form"]');
      const submitButton = form?.querySelector(
        'button[type="submit"]',
      );
      submitButton?.click();
    }

    handleVariantChange(event) {
      if (event.detail) {
        this.updatePricingFromVariant(event.detail);
      }
      this.syncAvailabilityFromProductForm(event.detail?.available);
      this.queuePricingSync();
    }

    handlePriceUpdate() {
      this.queuePricingSync();
    }

    queuePricingSync() {
      window.clearTimeout(this.pricingTimer);
      this.pricingTimer = window.setTimeout(() => {
        this.updatePricingFromDOM();
      }, 100);
    }

    handleScroll() {
      if (this.ticking) {
        return;
      }

      this.ticking = true;
      window.requestAnimationFrame(() => {
        if (this.destroyed) {
          this.ticking = false;
          return;
        }

        this.syncVisibility();
        this.ticking = false;
      });
    }

    handleViewportChange() {
      this.handleScroll();
    }

    isDesktopStickyDisabled() {
      return (
        this.container.dataset.stickyCtaEnableDesktop === "false" &&
        window.matchMedia("(min-width: 750px)").matches
      );
    }

    syncVisibility() {
      const buyButton = getBuyButton();
      if (!buyButton || !this.element) {
        return;
      }

      const hasScrolledPastButton =
        buyButton.getBoundingClientRect().bottom < 0;
      const hasReachedSection =
        this.container.getBoundingClientRect().top <= window.innerHeight;
      const shouldShow =
        hasScrolledPastButton &&
        !hasReachedSection &&
        !this.isDesktopStickyDisabled();

      this.element.classList.toggle(visibleClass, shouldShow);
      this.element.setAttribute("aria-hidden", shouldShow ? "false" : "true");
      this.updateHubspotOffset(shouldShow);
    }

    updatePricingFromDOM() {
      const buyButtonPriceElement = document.querySelector("#buy-button-price");
      const priceContainer = document.querySelector(
        ".price__container[data-discounted-price-cents]",
      );

      if (buyButtonPriceElement && this.priceElement) {
        this.priceElement.textContent =
          buyButtonPriceElement.textContent.trim();
      }

      if (priceContainer) {
        const totalPrice =
          parseMoneyToCents(buyButtonPriceElement?.textContent) ||
          Number.parseInt(
            priceContainer.dataset.discountedPriceCents || "0",
            10,
          );
        const originalPrice = Number.parseInt(
          priceContainer.dataset.originalPriceCents || "0",
          10,
        );
        this.updateRetailPercent(totalPrice, originalPrice);
        this.updateAffirmPromos(totalPrice);
      }
    }

    updatePricingFromVariant(variant) {
      const price = Number.parseInt(variant.price || "0", 10);
      const compareAtPrice = Number.parseInt(variant.compareAtPrice || "0", 10);

      if (this.priceElement && variant.formattedPrice) {
        this.priceElement.textContent = variant.formattedPrice;
      } else if (this.priceElement && price > 0) {
        this.priceElement.textContent = formatCurrency(price);
      }

      this.updateRetailPercent(price, compareAtPrice);
      this.updateAffirmPromos(price);
    }

    updateAffirmPromos(price) {
      if (price <= 0) {
        return;
      }

      this.affirmAmountElements.forEach((element) => {
        element.dataset.amount = String(price);
      });
      refreshAffirm();
    }

    updateRetailPercent(price, compareAtPrice) {
      if (!this.percentElement || !this.percentValueElement) {
        return;
      }

      const hasRetailPercent = compareAtPrice > 0 && price > 0;
      this.percentElement.classList.toggle(hiddenClass, !hasRetailPercent);
      if (hasRetailPercent) {
        this.percentValueElement.textContent = `${Math.round(
          (price / compareAtPrice) * 100,
        )}%`;
      }
    }

    syncAvailabilityFromProductForm(fallbackIsAvailable) {
      const state = getBuyButtonState(this.observedBuyButton || getBuyButton());

      if (state) {
        this.updateAvailability(!state.disabled, state.label);
        this.observeBuyButton(state.button);
        return;
      }

      if (typeof fallbackIsAvailable !== "undefined") {
        this.updateAvailability(Boolean(fallbackIsAvailable));
      }
    }

    observeBuyButton(button = getBuyButton()) {
      if (!button || this.observedBuyButton === button) {
        return;
      }

      this.buyButtonObserver?.disconnect();
      this.observedBuyButton = button;
      this.buyButtonObserver = new MutationObserver(() => {
        this.syncAvailabilityFromProductForm();
      });
      this.buyButtonObserver.observe(button, {
        attributes: true,
        attributeFilter: ["class", "disabled", "aria-disabled"],
        childList: true,
        subtree: true,
      });
    }

    updateAvailability(isAvailable, label) {
      if (this.submitButton) {
        this.submitButton.disabled = !isAvailable;
      }

      if (this.submitButtonLabel) {
        this.submitButtonLabel.textContent =
          label ||
          (isAvailable
            ? this.submitButtonLabel.dataset.addLabel
            : this.submitButtonLabel.dataset.soldOutLabel);
      }
    }

    updateHubspotOffset(shouldShow) {
      const hubspotContainer = document.querySelector(
        "#hubspot-messages-iframe-container",
      );
      if (!hubspotContainer) {
        return;
      }

      if (shouldShow && this.element) {
        hubspotContainer.style.setProperty(
          "bottom",
          `${this.element.offsetHeight}px`,
          "important",
        );
        hubspotContainer.style.setProperty("z-index", "1", "important");
        hubspotContainer.style.setProperty(
          "transition",
          "bottom 0.3s ease-in-out",
          "important",
        );
      } else {
        hubspotContainer.style.removeProperty("bottom");
        hubspotContainer.style.removeProperty("z-index");
        hubspotContainer.style.removeProperty("transition");
      }
    }
  }

  const initStickyCTAs = (root = document) => {
    root.querySelectorAll(containerSelector).forEach((container) => {
      container.stickyCTA?.destroy();
      delete container.dataset.stickyCtaInitialized;
      new StickyCTA(container).init();
    });
  };

  const destroyStickyCTAs = (root = document) => {
    root.querySelectorAll(containerSelector).forEach((container) => {
      container.stickyCTA?.destroy();
    });
  };

  document.addEventListener("DOMContentLoaded", () => initStickyCTAs());
  document.addEventListener("shopify:section:load", (event) => {
    initStickyCTAs(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    destroyStickyCTAs(event.target);
  });
})();
